/**
 * Цвет имени участника — как в Discord: цвет старшей из его ролей, у которой
 * цвет есть. Нет такой — `null`, и имя остаётся обычным.
 */
export function roleColorOf(
  memberRoles: readonly string[],
  roles: readonly { id: string; rank: number; color?: string | null }[],
): string | null {
  let best: { rank: number; color: string } | null = null;
  for (const role of roles) {
    if (!role.color || !memberRoles.includes(role.id)) continue;
    if (!best || role.rank > best.rank) best = { rank: role.rank, color: role.color };
  }
  return best?.color ?? null;
}

/** Значок старшей роли со значком — как цвет, но эмодзи. */
export function roleIconOf(
  memberRoles: readonly string[],
  roles: readonly { id: string; rank: number; icon?: string | null }[],
): string | null {
  let best: { rank: number; icon: string } | null = null;
  for (const role of roles) {
    if (!role.icon || !memberRoles.includes(role.id)) continue;
    if (!best || role.rank > best.rank) best = { rank: role.rank, icon: role.icon };
  }
  return best?.icon ?? null;
}

/**
 * Группы списка участников — как в Discord: кто в сети, тот стоит под
 * старшей из своих ролей с «показывать отдельно»; у кого такой нет — под
 * «в сети». Кто не в сети — одной группой, без разбивки по ролям.
 */
export function memberGroups<M extends { roles: readonly string[] }>(
  members: readonly M[],
  roles: readonly { id: string; name: string; rank: number; hoist?: boolean }[],
  online: (member: M) => boolean,
): { title: string; members: M[]; dim: boolean }[] {
  const hoisted = roles.filter((role) => role.hoist).sort((a, b) => b.rank - a.rank);
  const groups = new Map<string, M[]>(hoisted.map((role) => [role.id, []]));
  const rest: M[] = [];
  const away: M[] = [];

  for (const member of members) {
    if (!online(member)) {
      away.push(member);
      continue;
    }
    const lead = hoisted.find((role) => member.roles.includes(role.id));
    (lead ? groups.get(lead.id)! : rest).push(member);
  }

  return [
    ...hoisted.map((role) => ({ title: role.name.toUpperCase(), members: groups.get(role.id)!, dim: false })),
    { title: "В СЕТИ", members: rest, dim: false },
    { title: "НЕ В СЕТИ", members: away, dim: true },
  ].filter((group) => group.members.length > 0);
}
